import fireflyIcon from "@/assets/firefly-icon.svg";
import lemurIcon from "@/assets/lemur-icon.svg";
import beeIcon from "@/assets/bee-icon.svg";
import gaiaIcon from "@/assets/gaia-icon.svg";
import fireantIcon from "@/assets/fireant-icon.svg";
import koalaIcon from "@/assets/koala-icon.svg";

export type Product = {
  name: string;
  tagline: string;
  problem: string;
  benefits: string[];
  positioning: string;
  slug: string;
  seoTitle: string;
  metaDescription: string;
  icon: string;
  alt: string;
  accent: string;
};

export const products: Product[] = [
  {
    name: "FireFly",
    tagline: "High-Performance Integration Platform",
    problem: "A production-grade platform delivering low-latency services and APIs for real-time integration and orchestration across systems, devices, and sensors.",
    benefits: [
      "**Lightning Fast:** Real-time processing with sub-5ms latency for instant execution across services, APIs, and distributed data flows",
      "**Cloud-Native:** Lightweight Docker-based deployment for flexible use across on-premises and cloud environments",
      "**High Reliability:** Built for continuous operation with 99.99% uptime, ensuring stable performance under sustained load",
      "**Scalable:** Seamlessly scales across distributed environments to support high-throughput and growing system demands",
    ],
    positioning: "GDPR-compliant · In-memory processing & Caching · End-to-end data encryption",
    slug: "firefly",
    seoTitle: "FireFly® Integration Platform | Real-Time Data Integration, Low-Latency APIs | Bane Tech",
    metaDescription: "FireFly® is a high-performance enterprise integration platform delivering sub-5ms, low-latency APIs and real-time orchestration across systems, devices and sensors.",
    icon: fireflyIcon,
    alt: "FireFly Enterprise Integration Platform",
    accent: "from-primary to-primary-glow",
  },
  {
    name: "Lemur Monitor",
    tagline: "System Observability & Performance Monitoring",
    problem: "Real-time visibility into system behaviour, performance, and operational health across your entire infrastructure.",
    benefits: [
      "**Real-Time Observability:** Live monitoring of FireFly®, infrastructure, and backend systems with full ecosystem-wide visibility",
      "**Advanced Analytics:** Time-based metrics, payload analysis, and detailed performance insights including HTTP status, throughput, and request patterns",
      "**Intelligent Operations:** AI-assisted insights for error classification, health monitoring, and operational decision support across services and integrations",
      "**Governance & Alerting:** SLA/OLA tracking, intelligent alerting, and integrations with tools such as Jira and Microsoft Teams",
    ],
    positioning: "Metrics aggregation & Time-series analytics · AI-Driven anomaly detection · Event-based alerting & Integration",
    slug: "lemur-monitor",
    seoTitle: "Lemur Monitor® | Real-Time Observability & Performance Monitoring | Bane Tech",
    metaDescription: "Lemur Monitor® delivers real-time observability, performance analytics, AI-driven anomaly detection and SLA alerting across your entire infrastructure.",
    icon: lemurIcon,
    alt: "Lemur Monitor Enterprise Observability Platform",
    accent: "from-accent to-primary-glow",
  },
  {
    name: "Bee Portfolio",
    tagline: "Enterprise Architecture & Portfolio Intelligence",
    problem: "A unified view of systems and services that enables governance, transparency, and informed strategic decision-making.",
    benefits: [
      "**Real-Time Enterprise Architecture:** Continuous, live representation of systems, services, and dependencies as they evolve",
      "**Ecosystem & Business Flow Mapping:** Graphical visualization of systems and business processes with end-to-end ecosystem visibility",
      "**Automated Catalogs & Service Intelligence:** Automatically generated catalogs of services, applications, devices, and sensors with usage and cost insights",
      "**Impact Analysis & Omnichannel Insights:** Dynamic impact assessment with aggregated and segmented insights across business channels and domains",
    ],
    positioning: "Live architecture modelling · Graph-based dependency mapping · Real-time data aggregation",
    slug: "bee-portfolio",
    seoTitle: "Bee Portfolio® | Real-Time Enterprise Architecture & Portfolio Management | Bane Tech",
    metaDescription: "Bee Portfolio® provides live enterprise architecture, dependency mapping, automated service catalogs and impact analysis for informed IT governance.",
    icon: beeIcon,
    alt: "Bee Portfolio Enterprise Architecture Management",
    accent: "from-primary to-accent",
  },
  {
    name: "Gaia Entity",
    tagline: "Polymorphic AI for real-time decision intelligence",
    problem: "Delivers adaptive AI neurons that continuously capture and correlate metrics across services and integrations, enabling autonomous decision-making and rapid anomaly detection at scale.",
    benefits: [
      "**Real-time Correlation:** Unifies telemetry across systems into a live, coherent view of behaviour and dependencies",
      "**Autonomous Decisions:** AI-driven responses to changing system conditions, executed instantly and continuously",
      "**Anomaly Detection:** Identifies and classifies behavioural deviations across distributed environments in real time",
      "**System-wide Intelligence:** Aggregates insights across services to enable consistent, data-driven decisions at scale",
    ],
    positioning: "Real-Time correlation · Autonomous AI · Polymorphic intelligence",
    slug: "gaia-entity",
    seoTitle: "Gaia Entity® | Polymorphic AI for Real-Time Decision Intelligence | Bane Tech",
    metaDescription: "Gaia Entity® uses adaptive polymorphic AI to correlate telemetry in real time, enabling autonomous decisions and anomaly detection at scale.",
    icon: gaiaIcon,
    alt: "Gaia Entity AI Operational Intelligence",
    accent: "from-accent to-primary",
  },
  {
    name: "FireAnt",
    tagline: "Real-time change data capture and data movement",
    problem: "Delivers high-performance Change Data Capture and events that synchronise data across distributed systems with low latency and high reliability.",
    benefits: [
      "**Real-time Change Capture:** Continuously detects and propagates data changes at the source for immediate downstream availability",
      "**Low-latency Events:** Real time events across systems for fast, reliable synchronisation",
      "**Consistent Synchronisation:** Maintains accurate, up-to-date data across all connected services and environments",
      "**Scalable Data Movement:** Handles high-throughput data changes across complex, distributed architectures with reliability",
    ],
    positioning: "Log-based CDC · Event-driven · Distributed synchronisation",
    slug: "fireant",
    seoTitle: "FireAnt® | Real-Time Change Data Capture (CDC) & Data Sync | Bane Tech",
    metaDescription: "FireAnt® delivers log-based change data capture and low-latency events to synchronise data reliably across distributed systems in real time.",
    icon: fireantIcon,
    alt: "FireAnt Change Data Capture (CDC) Platform",
    accent: "from-primary-glow to-accent",
  },
  {
    name: "Koala Flow",
    tagline: "Enterprise Project & Integration Delivery Management",
    problem: "A unified platform for managing programs, projects, suppliers, teams, and integration delivery, providing real-time visibility and controlled access across the entire delivery lifecycle.",
    benefits: [
      "**Centralised Delivery Management:** Organises programs and projects with project managers, suppliers, team leaders, and delivery teams in a single operational view",
      "**Controlled Team Collaboration:** Enables team leaders to manage project members while developers access only the services, documentation, and integration resources designated for their projects",
      "**Integration Visibility:** Gives developers direct visibility into their own FireFly® requests and integration status, enabling them to confirm successful integrations without relying on meetings or manual reporting",
      "**Governance & Operational Intelligence:** Provides project and program managers with real-time control, delivery statistics, team visibility, and end-to-end insight into integration progress across projects and suppliers",
    ],
    positioning: "Program & Project governance · Controlled developer access · Real-Time integration visibility",
    slug: "koala-flow",
    seoTitle: "Koala Flow® | Enterprise Project & Integration Delivery Management | Bane Tech",
    metaDescription: "Koala Flow® unifies program, project, supplier and integration delivery management with controlled developer access and real-time visibility.",
    icon: koalaIcon,
    alt: "Koala Flow Enterprise Project Management",
    accent: "from-primary-glow to-primary",
  },
];

